import React, { useRef, useState } from "react"

import useOnclickOutside from "react-cool-onclickoutside"

import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import type { IButtonDropdownOption } from "../types/objects.types"

export const ButtonDropdownElement: React.FunctionComponent<
    IButtonDropdownElementProps
> = props => {
    const containerRef = useOnclickOutside(() => {
        setIsDropdownActive(false)
    })

    const [isDropdownActive, setIsDropdownActive] = useState<boolean>(false)

    const menuRef = useRef<HTMLDivElement>(null)

    const toggleDropdown = () => {
        setIsDropdownActive(current => !current)
    }

    // Focused option goes away with menu, so focus goes back to button, which is first in container
    const closeDropdown = () => {
        if (menuRef.current?.contains(document.activeElement)) {
            menuRef.current.parentElement?.querySelector("button")?.focus()
        }

        setIsDropdownActive(false)
    }

    const onOptionClick = (item: IButtonDropdownOption) => {
        closeDropdown()
        item.onClick()
    }

    return (
        <div
            ref={containerRef}
            className={`relative ${props.className ?? ""}`}
            onKeyDown={event => {
                if (event.key === "Escape" && isDropdownActive) {
                    closeDropdown()
                }
            }}
            // Menu doesn't stay over the page when keyboard focus leaves it, without next focused element
            // it's a click on menu itself or away from window, clicks outside are handled separately
            onBlur={event => {
                if (
                    event.relatedTarget &&
                    !event.currentTarget.contains(event.relatedTarget)
                ) {
                    setIsDropdownActive(false)
                }
            }}
        >
            <ButtonElement
                label={props.buttonLabel}
                onClick={toggleDropdown}
                htmlType="button"
                type={props.buttonType}
                icon={props.buttonIcon}
                isRightIcon={props.buttonIsRightIcon}
                isLoading={props.buttonIsLoading}
                disabled={props.buttonIsDisabled}
                buttonClassName={props.buttonClassName}
            />

            {isDropdownActive && (
                <div
                    ref={menuRef}
                    role={props.dropdownTitle ? "group" : undefined}
                    aria-label={props.dropdownTitle}
                    className={`
                        absolute z-100 bg-brand-white right-0 top-[56px]
                        rounded-[8px] shadow-dropdown-menu border border-solid border-brand-warmLight
                        overflow-hidden
                        ${props.dropdownClassName ?? "w-[200px]"}
                    `}
                >
                    {/*Menu is labeled with it already*/}
                    {props.dropdownTitle && (
                        <div aria-hidden="true">
                            <PrimaryText
                                typography="captionSemibold"
                                className="px-[16px] pb-[4px] pt-[12px] opacity-60"
                            >
                                {props.dropdownTitle}
                            </PrimaryText>
                        </div>
                    )}

                    {props.options.map((item, index) => {
                        return (
                            <button
                                key={index}
                                type="button"
                                disabled={item.isDisabled}
                                aria-pressed={item.isSelected}
                                // Menu clips outline, so it's drawn inside
                                className={`
                                    block w-full border-0 p-[16px] text-left focus-visible:outline-offset-[-2px]
                                    ${item.isSelected ? "bg-brand-offWhite" : "bg-transparent"}
                                    ${item.isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-brand-lilacLight"}
                                `}
                                onClick={() => {
                                    onOptionClick(item)
                                }}
                            >
                                <PrimaryText typography="buttonText">
                                    {item.label}
                                </PrimaryText>
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

interface IButtonDropdownElementProps {
    className?: string
    buttonLabel: string
    options: IButtonDropdownOption[]
    buttonType?: "default" | "primary" | "ghost" | "dashed" | "link" | "text"
    buttonIcon?: string | React.ReactNode
    buttonIsLoading?: boolean
    buttonIsDisabled?: boolean
    buttonIsRightIcon?: boolean
    buttonClassName?: string
    dropdownClassName?: string
    dropdownTitle?: string // shown above options
}
